// /exchange-rates — Exchange Rate Management (PAGES.md §3.104)
//
// Manage currency exchange rates for multi-currency billing (API §43).
// Configure conversion rates between currencies (ISO 4217) with effective dates.
// SHARED vertical. No feature flag. Min role: MANAGER (30) view, OWNER (50) create/edit/delete.
// Traceability: FR-BILL-050

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Select,
  Input,
  DatePicker,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  DataTable,
  Pagination,
  Modal,
  ConfirmDialog,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { ROLE_LEVELS } from '@/types/user';
import { useAuthStore } from '@/stores/use-auth-store';
import { exchangeRateService } from '@/services/exchange-rate.service';
import type { ExchangeRate, CreateExchangeRateDto, UpdateExchangeRateDto } from '@/services/exchange-rate.service';
import { ApiError } from '@/lib/api-client';
import { GLASS, GLASS_FILTER, GLASS_INTERACTIVE, cn, toneClasses } from '@/constants/design-tokens';
import { toLocalIsoDate } from './date-utils';
import { PURCHASE_CURRENCIES } from '@bbpos/types';

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

// The 21 purchase currencies (docs/multi-currency-purchasing-plan.md D6): the
// API accepts these only, and purchase orders read their rates from here.
const CURRENCIES = PURCHASE_CURRENCIES.map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` }));

const CURRENCY_FILTER_OPTIONS = [
  { value: '', label: 'All Currencies' },
  ...CURRENCIES,
];

const ISO_CODE_REGEX = /^[A-Z]{3}$/;

// Rate precision/bound — kept in sync with the backend DTO (NUMERIC(18,8)):
// up to 8 decimal places, max value 1,000,000.
const RATE_DECIMAL_PLACES = 8;
const MAX_EXCHANGE_RATE = 1_000_000;

// ─── Icons ─────────────────────────────────────────────────────────────────

function CurrencySwapIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
    </svg>
  );
}

function CurrencyEmptyIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

function currencyLabel(code: string): string {
  const found = CURRENCIES.find((c) => c.value === code);
  return found ? found.label : code;
}

// ─── Mobile Card ────────────────────────────────────────────────────────────

function RateMobileCard({
  rate,
  canEdit,
  onEdit,
  onDelete,
}: {
  rate: ExchangeRate;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className={cn(GLASS_INTERACTIVE, 'p-4 animate-slide-up')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge variant="info" size="sm">{rate.from_currency}</Badge>
            <span className="text-neutral-400 dark:text-neutral-500">→</span>
            <Badge variant="active" size="sm">{rate.to_currency}</Badge>
          </div>
          <p className="text-lg font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100 mt-2">
            1 {rate.from_currency} = {rate.rate.toFixed(RATE_DECIMAL_PLACES)} {rate.to_currency}
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="p-2 rounded-lg text-neutral-500 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
              aria-label="Edit rate"
            >
              <PencilIcon />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-2 rounded-lg text-neutral-500 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
              aria-label="Delete rate"
            >
              <TrashIcon />
            </button>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-neutral-500 dark:text-neutral-400">
          Effective: {formatDate(rate.effective_date)}
        </span>
        <span className="text-xs text-neutral-400 dark:text-neutral-500">
          Updated: {formatDate(rate.updated_at)}
        </span>
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function ExchangeRatesContent() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Role
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const canModify = roleLevel >= ROLE_LEVELS.OWNER;

  // URL-synced filters
  const urlFrom = searchParams.get('from_currency') ?? '';
  const urlTo = searchParams.get('to_currency') ?? '';
  const urlPage = Number(searchParams.get('page') ?? '1');

  const [fromFilter, setFromFilter] = useState(urlFrom);
  const [toFilter, setToFilter] = useState(urlTo);
  const [page, setPage] = useState(urlPage);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (fromFilter) params.from_currency = fromFilter;
    if (toFilter) params.to_currency = toFilter;
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [fromFilter, toFilter, page, setSearchParams]);

  // Data
  const [rates, setRates] = useState<ExchangeRate[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await exchangeRateService.list({
        page,
        from_currency: fromFilter || undefined,
        to_currency: toFilter || undefined,
      });
      setRates(result.data);
      setTotalItems(result.meta.total);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load exchange rates.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [page, fromFilter, toFilter, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Create/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<ExchangeRate | null>(null);
  const [formFrom, setFormFrom] = useState('INR');
  const [formTo, setFormTo] = useState('');
  const [formRate, setFormRate] = useState('');
  const [formDate, setFormDate] = useState(todayISO());
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState<ExchangeRate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openCreateModal = useCallback(() => {
    setEditingRate(null);
    setFormFrom('INR');
    setFormTo('');
    setFormRate('');
    setFormDate(todayISO());
    setFormError(null);
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback((rate: ExchangeRate) => {
    setEditingRate(rate);
    setFormFrom(rate.from_currency);
    setFormTo(rate.to_currency);
    setFormRate(String(rate.rate));
    setFormDate(rate.effective_date.split('T')[0]);
    setFormError(null);
    setModalOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    // Validation
    if (!ISO_CODE_REGEX.test(formFrom)) { setFormError('From currency must be a valid 3-letter ISO 4217 code'); return; }
    if (!ISO_CODE_REGEX.test(formTo)) { setFormError('To currency must be a valid 3-letter ISO 4217 code'); return; }
    if (formFrom === formTo) { setFormError('From and To currencies must be different'); return; }
    const rateNum = Number(formRate);
    if (!Number.isFinite(rateNum) || rateNum <= 0) { setFormError('Exchange rate must be greater than zero'); return; }
    if (rateNum > MAX_EXCHANGE_RATE) { setFormError(`Exchange rate must not exceed ${MAX_EXCHANGE_RATE.toLocaleString('en-IN')}`); return; }
    // Backend validates rate with @IsNumberString — send a normalized numeric string, not a JSON number.
    const rateStr = String(rateNum);
    if (!formDate) { setFormError('Effective date is required'); return; }

    setIsSaving(true);
    setFormError(null);

    try {
      if (editingRate) {
        const dto: UpdateExchangeRateDto = { rate: rateStr, effective_date: formDate };
        await exchangeRateService.update(editingRate.id, dto);
        toast({ title: 'Exchange rate updated', variant: 'success' });
      } else {
        const dto: CreateExchangeRateDto = {
          from_currency: formFrom,
          to_currency: formTo,
          rate: rateStr,
          effective_date: formDate,
        };
        await exchangeRateService.create(dto);
        toast({ title: `Exchange rate ${formFrom} → ${formTo} saved`, variant: 'success' });
      }
      setModalOpen(false);
      fetchData();
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 409) {
        setFormError('Rate for this currency pair already exists for this date.');
      } else {
        setFormError(err instanceof ApiError ? err.message : 'Failed to save exchange rate');
      }
    } finally {
      setIsSaving(false);
    }
  }, [formFrom, formTo, formRate, formDate, editingRate, fetchData, toast]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await exchangeRateService.delete(deleteTarget.id);
      toast({ title: 'Exchange rate deleted', variant: 'success' });
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to delete exchange rate', variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, fetchData, toast]);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.key === 'n' && !modalOpen && !deleteTarget && canModify) {
        e.preventDefault();
        openCreateModal();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, deleteTarget, canModify, openCreateModal]);

  useEffect(() => { document.title = 'Exchange Rates — Orio360 Admin'; }, []);

  // KPI stats
  const stats = useMemo(() => {
    const totalPairs = totalItems;
    const lastUpdated = rates.length > 0
      ? formatDate(rates.reduce((latest, r) => r.updated_at > latest ? r.updated_at : latest, rates[0].updated_at))
      : '—';
    const activeRates = rates.length;
    return { totalPairs, lastUpdated, activeRates };
  }, [rates, totalItems]);

  // Table columns
  const columns: ColumnDef<ExchangeRate>[] = useMemo(() => {
    const cols: ColumnDef<ExchangeRate>[] = [
      {
        key: 'from_currency', header: 'From',
        render: (_v: unknown, row: ExchangeRate) => (
          <Badge variant="info" size="sm">{row.from_currency}</Badge>
        ),
      },
      {
        key: 'to_currency', header: 'To',
        render: (_v: unknown, row: ExchangeRate) => (
          <Badge variant="active" size="sm">{row.to_currency}</Badge>
        ),
      },
      {
        key: 'rate', header: 'Rate', numeric: true,
        render: (_v: unknown, row: ExchangeRate) => (
          <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
            1 {row.from_currency} = {row.rate.toFixed(RATE_DECIMAL_PLACES)} {row.to_currency}
          </span>
        ),
      },
      {
        key: 'effective_date', header: 'Effective Date',
        render: (_v: unknown, row: ExchangeRate) => (
          <span className="text-sm text-neutral-600 dark:text-neutral-400">{formatDate(row.effective_date)}</span>
        ),
      },
      {
        key: 'updated_at', header: 'Last Updated',
        render: (_v: unknown, row: ExchangeRate) => (
          <span className="text-sm text-neutral-500 dark:text-neutral-400">{formatDate(row.updated_at)}</span>
        ),
      },
    ];

    if (canModify) {
      cols.push({
        key: 'actions', header: '',
        render: (_v: unknown, row: ExchangeRate) => (
          <div className="flex items-center gap-1 justify-end">
            <button
              type="button"
              onClick={() => openEditModal(row)}
              className="p-2 rounded-lg text-neutral-500 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
              aria-label={`Edit rate ${row.from_currency} → ${row.to_currency}`}
            >
              <PencilIcon />
            </button>
            <button
              type="button"
              onClick={() => setDeleteTarget(row)}
              className="p-2 rounded-lg text-neutral-500 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
              aria-label={`Delete rate ${row.from_currency} → ${row.to_currency}`}
            >
              <TrashIcon />
            </button>
          </div>
        ),
      });
    }

    return cols;
  }, [canModify, openEditModal]);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  // Conversion preview
  const conversionPreview = useMemo(() => {
    const r = Number(formRate);
    if (formFrom && formTo && Number.isFinite(r) && r > 0) {
      // Up to 8 places: an INR → OMR rate (0.0046) must not preview as 0.00.
      return `1 ${formFrom} = ${r.toLocaleString('en-US', { maximumFractionDigits: RATE_DECIMAL_PLACES })} ${formTo}`;
    }
    return '';
  }, [formFrom, formTo, formRate]);

  const brandTone = toneClasses('brand');
  const tealTone = toneClasses('teal');
  const successTone = toneClasses('success');

  return (
    <PageContainer
      title="Exchange Rates"
      subtitle="Manage currency conversion rates for multi-currency operations"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Exchange Rates' },
      ]}
      actions={
        canModify ? (
          <Button variant="primary" size="sm" onClick={openCreateModal}>
            <PlusIcon /><span className="ml-1.5">Add Exchange Rate</span>
          </Button>
        ) : undefined
      }
    >
      <div className="animate-fade-in">
        {/* Header Gradient */}
        <div className="bg-gradient-to-r from-brand-600 to-indigo-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(59,130,246,0.45)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
          <div className="relative">
            <h1 className="text-2xl font-bold text-white">Exchange Rates</h1>
            <p className="text-blue-100 mt-1">Manage currency conversion rates for multi-currency operations</p>
          </div>
        </div>

        {/* KPI Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className={cn(GLASS, 'p-5 animate-slide-up relative overflow-hidden')}>
            <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40 pointer-events-none', brandTone.accent)} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', brandTone.icon)}>
                  <CurrencySwapIcon className="h-5 w-5" />
                </div>
                <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Total Pairs</span>
              </div>
              <p className="text-2xl font-bold font-mono tabular-nums text-neutral-900 dark:text-white">
                {isLoading ? '—' : stats.totalPairs.toLocaleString('en-IN')}
              </p>
            </div>
          </div>

          <div className={cn(GLASS, 'p-5 animate-slide-up relative overflow-hidden')} style={{ animationDelay: '50ms' }}>
            <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40 pointer-events-none', tealTone.accent)} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', tealTone.icon)}>
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Last Updated</span>
              </div>
              <p className="text-lg font-bold text-neutral-900 dark:text-white">
                {isLoading ? '—' : stats.lastUpdated}
              </p>
            </div>
          </div>

          <div className={cn(GLASS, 'p-5 animate-slide-up relative overflow-hidden')} style={{ animationDelay: '100ms' }}>
            <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40 pointer-events-none', successTone.accent)} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', successTone.icon)}>
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Active Rates</span>
              </div>
              <p className="text-2xl font-bold font-mono tabular-nums text-neutral-900 dark:text-white">
                {isLoading ? '—' : stats.activeRates.toLocaleString('en-IN')}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={cn(GLASS_FILTER, 'p-4 mb-6')}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-48">
              <Select
                label="From Currency"
                options={CURRENCY_FILTER_OPTIONS}
                value={fromFilter}
                onChange={(val) => {
                  setFromFilter(typeof val === 'string' ? val : val[0] ?? '');
                  setPage(1);
                }}
                placeholder="All Currencies"
              />
            </div>
            <div className="w-48">
              <Select
                label="To Currency"
                options={CURRENCY_FILTER_OPTIONS}
                value={toFilter}
                onChange={(val) => {
                  setToFilter(typeof val === 'string' ? val : val[0] ?? '');
                  setPage(1);
                }}
                placeholder="All Currencies"
              />
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className={cn(GLASS, 'p-6 space-y-4')}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton variant="rect" width={60} height={24} />
                <Skeleton variant="rect" width={60} height={24} />
                <Skeleton variant="text" width="25%" height={18} />
                <Skeleton variant="text" width="15%" height={18} />
                <Skeleton variant="text" width="15%" height={18} />
              </div>
            ))}
          </div>
        ) : rates.length === 0 ? (
          <EmptyState
            variant="page"
            icon={<CurrencyEmptyIcon />}
            title="No exchange rates configured"
            description="Set up currency conversion rates for multi-currency operations"
            action={canModify ? { label: 'Add Exchange Rate', onClick: openCreateModal } : undefined}
          />
        ) : (
          <>
            {/* Desktop DataTable */}
            <div className={cn(GLASS, 'overflow-hidden hidden md:block mb-6')}>
              <div className="px-5 py-4">
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                  Exchange Rates ({totalItems.toLocaleString('en-IN')})
                </h3>
              </div>
              <DataTable<ExchangeRate> columns={columns} data={rates} />
            </div>

            {/* Mobile Cards */}
            <div className="block md:hidden space-y-3 mb-6">
              <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider px-1">
                Exchange Rates ({totalItems.toLocaleString('en-IN')})
              </h3>
              {rates.map((rate) => (
                <RateMobileCard
                  key={rate.id}
                  rate={rate}
                  canEdit={canModify}
                  onEdit={() => openEditModal(rate)}
                  onDelete={() => setDeleteTarget(rate)}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mb-6">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Mobile FAB */}
      {canModify && (
        <button
          type="button"
          onClick={openCreateModal}
          className="fixed bottom-6 right-6 md:hidden z-40 w-14 h-14 rounded-full bg-gradient-to-br from-brand-500 to-indigo-600 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center active:scale-95"
          aria-label="Add Exchange Rate"
        >
          <PlusIcon />
        </button>
      )}

      {/* Create/Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingRate ? 'Edit Exchange Rate' : 'Add Exchange Rate'}
      >
        <div className="space-y-4 p-1 max-h-[70vh] overflow-y-auto">
          {formError && (
            <div className="text-xs text-danger-600 bg-danger-50 dark:bg-danger-900/20 px-3 py-2 rounded-lg">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                From Currency *
              </label>
              {editingRate ? (
                <div className="px-3 py-2 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  {currencyLabel(formFrom)}
                </div>
              ) : (
                <Select
                  options={CURRENCIES}
                  value={formFrom}
                  onChange={(val) => { setFormFrom(typeof val === 'string' ? val : val[0] ?? ''); setFormError(null); }}
                  placeholder="Select currency"
                />
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                To Currency *
              </label>
              {editingRate ? (
                <div className="px-3 py-2 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  {currencyLabel(formTo)}
                </div>
              ) : (
                <Select
                  options={CURRENCIES.filter((c) => c.value !== formFrom)}
                  value={formTo}
                  onChange={(val) => { setFormTo(typeof val === 'string' ? val : val[0] ?? ''); setFormError(null); }}
                  placeholder="Select currency"
                />
              )}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
              Exchange Rate *
            </label>
            <Input
              type="number"
              value={formRate}
              onChange={(val) => { setFormRate(val); setFormError(null); }}
              placeholder="e.g. 83.50"
            />
            {conversionPreview && (
              <p className="text-xs text-brand-600 dark:text-brand-400 mt-1" id="rate-preview">
                {conversionPreview}
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
              Effective Date
            </label>
            <DatePicker
              value={formDate ? new Date(`${formDate}T00:00:00`) : undefined}
              onChange={(d: Date) => setFormDate(toLocalIsoDate(d))}
            />
            <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-1">Defaults to today. Past dates allowed for historical corrections.</p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : editingRate ? 'Update Rate' : 'Create Rate'}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Exchange Rate"
        description={`Are you sure you want to delete the exchange rate for ${deleteTarget?.from_currency} → ${deleteTarget?.to_currency}? This cannot be undone.`}
        confirmLabel="Delete Rate"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={isDeleting}
      />
    </PageContainer>
  );
}

export function ExchangeRatesPage() {
  return (
    <ErrorBoundary>
      <ExchangeRatesContent />
    </ErrorBoundary>
  );
}
